import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "../cx";
import { Kbd } from "../primitives/Text";

/** Sidepane del sistema (la cola HITL vive aquí). El área de grid la da la página. */
export function Sidepane({ title, count, hot, keys, children, className, ...rest }: { title: ReactNode; count?: ReactNode | undefined; hot?: boolean | undefined; keys?: ReactNode | undefined; children: ReactNode; className?: string | undefined } & HTMLAttributes<HTMLElement>) {
  return (
    <aside className={cx("sk-sidepane", className)} {...rest}>
      <div className="sk-sidepane-h">
        <span className="sk-overline">{title}</span>
        {count !== undefined && <span className={cx("sk-count", hot && "sk-count--hot")}>{count}</span>}
        {keys}
      </div>
      <div className="sk-sidepane-b">{children}</div>
    </aside>
  );
}
/**
 * Panel lateral POR SECCIONES (biblioteca.html, 2026-10-26): dos o más
 * cabeceras en la misma columna —«Uso en el Despacho» encima de «Mi
 * revisión»—. Hace scroll la columna entera, no cada cuerpo; cada sección
 * lleva su `aria-label` (su título, si es texto). El área de grid la da la página.
 */
export function SidepaneStack({ children, className, ...rest }: { children: ReactNode; className?: string | undefined } & HTMLAttributes<HTMLElement>) {
  return <aside className={cx("sk-sidepane", "sk-sidepane--sections", className)} {...rest}>{children}</aside>;
}
/** Una sección de `SidepaneStack`: la misma cabecera (título, contador, teclas) y su cuerpo. */
export function SidepaneSection({ title, label, count, hot, keys, children, ...rest }: { title: ReactNode; label?: string | undefined; count?: ReactNode | undefined; hot?: boolean | undefined; keys?: ReactNode | undefined; children: ReactNode } & Omit<HTMLAttributes<HTMLElement>, "title">) {
  return (
    <section className="sk-sidepane-sec" aria-label={label ?? (typeof title === "string" ? title : undefined)} {...rest}>
      <div className="sk-sidepane-h">
        <span className="sk-overline">{title}</span>
        {count !== undefined && <span className={cx("sk-count", hot && "sk-count--hot")}>{count}</span>}
        {keys}
      </div>
      <div className="sk-sidepane-b">{children}</div>
    </section>
  );
}
/** Carril de prioridad de la cola (● P1 — hoy · ● P2 — esta semana).
    `p={0}`: carril sin prioridad («● Esperando a otros», biblioteca.html). */
export function QLane({ p = 1, children }: { p?: 0 | 1 | 2 | undefined; children: ReactNode }) {
  if (p === 0) return <div className="sk-qlane">● {children}</div>;
  return <div className="sk-qlane">{p === 1 ? <span className="sk-p1">● P1</span> : <>● P2</>} — {children}</div>;
}
/** `variant: "link"`: el acto que solo lleva a mirar («ver la ficha»), sin talla s.
    `stale`: apagado con porqué (sin conexión) — `aria-disabled` y fuera del teclado. */
export interface HitlAction { label: ReactNode; kbd?: string | undefined; tone?: "ok" | "danger" | undefined; variant?: "quiet" | "go" | "link" | undefined; stale?: boolean | undefined; onClick: () => void }
/** Tarjeta de cola HITL: tipo+SLA, título, evidencia/consecuencia. Con la
    tarjeta enfocada, la tecla de cada acción (kbd) la dispara.
    `interactive=false` (copia congelada de un estado de error — no se
    resuelve nada sin reconectar): sin foco ni acciones, misma lectura. */
export function HitlCard({ kind, act, sla, late, title, rows, actions, leaving, interactive = true }: {
  kind: ReactNode; act?: boolean | undefined; sla?: ReactNode | undefined; late?: boolean | undefined;
  title: ReactNode; rows: ReadonlyArray<{ k: ReactNode; v: ReactNode }>; actions: ReadonlyArray<HitlAction>; leaving?: boolean | undefined; interactive?: boolean | undefined;
}) {
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey) return;
    const a = actions.find((x) => x.stale !== true && x.kbd?.toLowerCase() === e.key.toLowerCase());
    if (a) { e.preventDefault(); a.onClick(); }
  };
  return (
    <article className={cx("sk-hitl", leaving && "sk-gone")} tabIndex={interactive ? 0 : undefined} onKeyDown={interactive ? onKeyDown : undefined}>
      <div className={cx("sk-hitl-kind", act && "sk-hitl-kind--act")}>
        {kind}
        {sla !== undefined && <span className={cx("sk-sla", late && "sk-late")}>{sla}</span>}
      </div>
      <h4>{title}</h4>
      {rows.map((r, i) => (
        <div className="sk-hitl-row" key={i}><span className="sk-k">{r.k}</span><span>{r.v}</span></div>
      ))}
      {interactive && (
        <div className="sk-hitl-acts">
          {actions.map((a, i) => (
            <button type="button" key={i} className={cx("sk-btn", a.variant !== "link" && "sk-btn--s", `sk-btn--${a.variant ?? "quiet"}`, a.tone === "ok" && "sk-hov-ok", a.tone === "danger" && "sk-hov-danger")} onClick={a.onClick}
              {...(a.stale === true && { "aria-disabled": true as const, tabIndex: -1 })}>
              {a.label} {a.kbd !== undefined && <Kbd>{a.kbd}</Kbd>}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}
